// src/components/LandingPage.tsx
import React, { useState, useMemo, useEffect } from 'react'; // Added useMemo
import Image from 'next/image';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MessageSquarePlus,  List , Settings, Users, MessageSquare } from 'lucide-react'; // Adjusted icons
import { EmissaryListCompact } from "@/components/EmissaryListCompact";
import EmissaryChoose from "@/components/EmissaryChoose"; // Assuming this is updated for Template
import ToolSelector from "@/components/tools/ToolSelector";
import type { Template } from '@/lib/firebase';
import type { AssistantDisplay, UserState } from '@/types'; // Use AssistantDisplay
import type { User } from 'firebase/auth';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import Link from 'next/link'; // Import Link for navigation


interface LandingPageProps {
  userState: UserState;
  currentUser: User | null;
  onShowLogin: () => void;
  onLogout: () => void; // Add logout handler prop
  // Assistants & Templates
  userAssistants: AssistantDisplay[];    // Renamed from existingBots, new type
  publicAssistants: AssistantDisplay[];   // Added prop for public assistants
  templates: Template[];                 // Updated type
  // Handlers
  onViewAssistant: (assistant: AssistantDisplay) => void; // Renamed from onViewBot, new type
  onCreateAssistant: (templateId: string) => void; 
}


const LandingPage: React.FC<LandingPageProps> = ({
  userState,
  currentUser,
  onShowLogin,
  onLogout,
  userAssistants = [],      
  publicAssistants = [], 
  templates = [],       
  onViewAssistant,    
  onCreateAssistant
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  // --- MODIFICATION START ---
  // Initialize activeTab based on userState
  const [activeTab, setActiveTab] = useState(() =>
    userState === 'not-logged-in' ? 'welcome' : 'my-assistants'
  );

  // Effect to update the active tab if the user logs in/out while on the page
  // and the current tab wouldn't be the new default.
  useEffect(() => {
    const newDefaultTab = userState === 'not-logged-in' ? 'welcome' : 'my-assistants';
    // Optional: Only switch if the current tab IS the old default.
    // You might prefer *not* to switch automatically if the user manually selected a tab.
    // Example: If logged out and on 'features', then logs in, stay on 'features'.
    // If logged out and on 'welcome', then logs in, switch to 'my-assistants'.
    // This logic keeps the current tab unless it was the specific default that needs changing.
    const oldDefaultTab = userState !== 'not-logged-in' ? 'welcome' : 'my-assistants';
     if (activeTab === oldDefaultTab) {
       setActiveTab(newDefaultTab);
     }
     // Simpler logic: Always reset to the new default on login/logout
     // setActiveTab(newDefaultTab);

  }, [userState, activeTab]); // Re-run when userState changes
  // --- MODIFICATION END ---

  // Read-only tools for demo
  const readOnlySelectedTools = useMemo(() => [
    'preview', 'highlights', 'plots', 'alerts', 'tooltips', 'file-browser', 'Google Search_retrieval', 'vertex_search'
  ], []);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    // If user is logged in, maybe switch to 'My Assistants' or directly go to create?
    // For now, just selecting is fine, user clicks 'Continue'
  };

  const handleContinueWithTemplate = () => {
    if (userState !== 'not-logged-in' && onCreateAssistant && selectedTemplate) {
      onCreateAssistant(selectedTemplate);
    } else if (userState === 'not-logged-in') {
      onShowLogin(); // Prompt login if trying to create while logged out
    }
  };

  // Assuming the welcome assistant is named 'Welcome to Emissary' or has a specific ID
  const welcomeAssistant = useMemo(() => {
    return publicAssistants.find(a => a.name === 'Welcome to Aitana'); // Adjust name/ID as needed
  }, [publicAssistants]);

  return (
    <div className="flex flex-col w-full max-w-6xl mx-auto px-4 py-8 sm:py-12 space-y-8 sm:space-y-12">
      {/* Hero section - Use generic branding */}
      <div className="text-center space-y-4">
        <div className="relative mx-auto w-64 h-20">
          {/* Use a generic logo or branding image */}
          <Image
            src="/images/logo/animated-aitana.svg"
            alt="Aitana Platform"
            fill
            className="object-contain"
          />
        </div>
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">Welcome to Aitana</h1>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto sm:text-xl">
          Creating powerful, specialised AI assistants for legal and contract work.
        </p>
         {/* Add Login/Logout/Admin buttons */}
         <div className="flex justify-center gap-4 mt-6">
           {userState === 'not-logged-in' ? (
             <Button size="lg" onClick={onShowLogin}>Sign In / Sign Up</Button>
           ) : (
             <>
               <Button size="lg" variant="outline" onClick={onLogout}>Logout</Button>
               <Button size="lg" asChild>
                 <Link href="/create">Create Assistant</Link>
               </Button>
               <Button size="lg" variant="outline" asChild>
                 <Link href="/admin">Admin Panel</Link>
               </Button>
             </>
           )}
         </div>
      </div>

      {/* Main content area */}
      <Tabs defaultValue="welcome" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="welcome">Welcome</TabsTrigger>
          <TabsTrigger value="features">Explore Tools</TabsTrigger>
          {/* Combine My Assistants and Templates based on login state */}
          <TabsTrigger value={userState === 'not-logged-in' ? "templates" : "my-assistants"}>
            {userState === 'not-logged-in' ? "Templates" : "My Aitana"}
          </TabsTrigger>
        </TabsList>

        {/* Welcome Tab */}
        <TabsContent value="welcome" className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>What is Aitana?</CardTitle>
              <CardDescription>
                An AI platform designed for legal professionals in the green energy sector.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Aitana allows you to build and share custom AI assistants equipped with specific instructions,
                documents, and tools to streamline tasks like document analysis, contract negotiation, and research.
              </p>

              {/* --- CHANGE 4: Update Welcome Tab Content --- */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {/* Card linking to the public Welcome Assistant */}
                {welcomeAssistant ? (
                  <Card className="p-4 border border-gray-200 flex flex-col">
                    <div className="flex items-start space-x-3 mb-4 flex-grow">
                      <Avatar>
                          <AvatarImage src={welcomeAssistant.avatar} alt={welcomeAssistant.name} />
                          <AvatarFallback>{welcomeAssistant.name ? welcomeAssistant.name[0] : 'W'}</AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-medium">{welcomeAssistant.name}</h3>
                        <p className="text-sm text-muted-foreground">Explore platform features and capabilities with our public demo assistant.</p>
                      </div>
                    </div>
                    <Button
                      className="w-full mt-auto"
                      onClick={() => onViewAssistant(welcomeAssistant)} // Use handler to open link
                    >
                      Chat with Welcome Assistant
                    </Button>
                  </Card>
                ) : (
                  <Card className="p-4 border border-gray-200 flex flex-col items-center justify-center text-center">
                      <Avatar>
                          <AvatarImage src="/images/avatars/aitana_help.jpeg" alt="Welcome to Aitana" />
                          <AvatarFallback>'W'</AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-medium">Talk to Aitana</h3>
                      </div>
                    <p className="text-muted-foreground">Log in to explore platform features and capabilities with our public demo assistant.</p>
                     {/* Optionally add a button to login */}
                  </Card>
                )}

                {/* Card for Creating Assistants / Logging In */}
                <Card className="p-4 border border-gray-200 flex flex-col">
                   <div className="flex items-start space-x-3 mb-4 flex-grow">
                     <div className="flex -space-x-2 overflow-hidden">
                         {/* Show template avatars or generic icons */}
                         {(templates.slice(0, 3) || []).map((t, i) => (
                            <Avatar key={i} className="inline-block h-10 w-10 rounded-full ring-2 ring-white">
                               <AvatarImage src={t.avatar} alt={t.name} />
                               <AvatarFallback>{t.name ? t.name[0] : 'A'}</AvatarFallback>
                            </Avatar>
                         ))}
                         {templates.length === 0 && <Avatar><AvatarFallback><MessageSquarePlus/></AvatarFallback></Avatar>}
                     </div>
                     <div className="pl-2">
                       <h3 className="font-medium">{userState === 'not-logged-in' ? 'Create an Account' : 'Create Custom Assistants'}</h3>
                       <p className="text-sm text-muted-foreground">
                         {userState === 'not-logged-in'
                           ? 'Sign up or log in to build and share specialized AI assistants.'
                           : 'Go to the Admin dashboard or select a template to start building.'}
                       </p>
                     </div>
                   </div>
                   <Button
                     className="w-full mt-auto"
                     variant="outline"
                     onClick={userState === 'not-logged-in' ? onShowLogin : () => window.location.href = '/create'} // Go to create or show login
                   >
                     {userState === 'not-logged-in' ? 'Sign In / Sign Up' : 'Create Assistant'}
                   </Button>
                 </Card>
              </div>
               {/* --- END CHANGE 4 --- */}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features Tab - Use ToolSelector, remove chat button */}
        <TabsContent value="features" className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Powerful Assistant Tools</CardTitle>
              <CardDescription>
                Equip your assistants with tools for enhanced functionality. Select tools when creating or editing assistants.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ToolSelector
                selectedTools={readOnlySelectedTools} // Show default tools
                onChange={() => {}} // No-op change handler for demo
                defaultOpen={true}
                //readOnly={true} // Make it visually clear it's read-only?
              />
              <div className="mt-8 text-center">
                 <p className="text-muted-foreground">Log in and go to the Admin page to configure tools for your assistants.</p>
                 {userState === 'not-logged-in' && (
                   <Button
                     variant="outline"
                     className="mt-4"
                     onClick={onShowLogin}
                   >
                     Sign In To Create & Configure
                   </Button>
                 )}
              </div>
               {/* --- END CHANGE 5 --- */}
            </CardContent>
          </Card>
        </TabsContent>

        {/* My Assistants Tab (only for logged-in users) */}
        {userState !== 'not-logged-in' && (
          <TabsContent value="my-assistants" className="space-y-6">
            <Card className="shadow-sm">
            <CardHeader className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 p-4 sm:p-6"> {/* Responsive direction, alignment, justify, gap, padding */}
              {/* Text content (remains the same) */}
              <div>
                <CardTitle>My Aitana Assistants</CardTitle>
                <CardDescription>
                  Access assistants shared with you or created by you.
                </CardDescription>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <Link href="/feed" passHref legacyBehavior>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                  >
                    <MessageSquare className="mr-1.5 h-4 w-4" />
                    Your Feed
                  </Button>
                </Link>
                <Link href="/" passHref legacyBehavior>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                  >
                    <Users className="mr-1.5 h-4 w-4" />
                    All Assistants
                  </Button>
                </Link>
                <Link href="/list" passHref legacyBehavior>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="shrink-0"
                  >
                    <List className="mr-1.5 h-4 w-4" />
                    View All
                  </Button>
                </Link>
              </div>
            </CardHeader>
              <CardContent className="px-4 pt-6 pb-4">
                {userAssistants && userAssistants.length > 0 ? (
                  <EmissaryListCompact
                    bots={userAssistants}
                    currentUser={currentUser}
                    onView={onViewAssistant}
                  />
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">No assistants found for your account.</p>
                    <Button
                      onClick={() => setActiveTab('templates')} // Switch to templates tab
                    >
                      Create Your First Assistant
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
             {/* Also show public assistants here for easy access? */}
             <Card className="shadow-sm">
                <CardHeader>
                   <CardTitle>Public Assistants</CardTitle>
                   <CardDescription>Assistants available to everyone.</CardDescription>
                </CardHeader>
                <CardContent>
                   {publicAssistants && publicAssistants.length > 0 ? (
                     <EmissaryListCompact bots={publicAssistants} currentUser={currentUser} onView={onViewAssistant} />
                   ) : (
                     <p className="text-muted-foreground text-center py-4">No public assistants available.</p>
                   )}
                </CardContent>
             </Card>
          </TabsContent>
        )}

        {/* Templates Tab (only for logged-out users) */}
        {userState === 'not-logged-in' && (
          <TabsContent value="templates" className="space-y-6">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Available Assistant Templates</CardTitle>
                <CardDescription>
                  Preview the types of specialized assistants you can create with an Aitana account.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <EmissaryChoose
                  templates={templates} // Pass formatted templates
                  selectedTemplate={selectedTemplate}
                  onSelect={handleTemplateSelect}
                />
                <div className="mt-8 text-center space-y-4">
                   <p className="text-muted-foreground">Sign in or sign up to create your own custom assistants from these templates.</p>
                   <Button onClick={onShowLogin}>Sign In / Sign Up</Button>
                </div>
              </CardContent>
            </Card>
            {/* Show Public Assistants for logged-out users too */}
             <Card className="shadow-sm">
               <CardHeader>
                 <CardTitle>Public Assistants</CardTitle>
                 <CardDescription>Try out assistants available to everyone.</CardDescription>
               </CardHeader>
               <CardContent>
                 {publicAssistants && publicAssistants.length > 0 ? (
                   <EmissaryListCompact bots={publicAssistants} currentUser={currentUser} onView={onViewAssistant} />
                 ) : (
                   <p className="text-muted-foreground text-center py-4">Log in to see the public assistants.</p>
                 )}
               </CardContent>
             </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default LandingPage;